// frontend/src/hooks/useEntitlement.js
import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "../lib/supabaseClient";
import { readEntitlementOverride } from "../lib/entitlementOverride";
import { fetchEffectivePlatformSecurityCapabilities } from "../lib/platformSecurityCapabilityService";

/**
 * Consolidated paywall note:
 * - App-level PaywallGuard is the PRIMARY gate for paid routes.
 * - This hook provides:
 *    - capability flags (reports/remediation)
 *    - plan identity (UI)
 *    - tier ("single" vs "top") for business entity limits
 *    - defensive checks during webhook lag
 *
 * Backwards compatibility:
 * - We KEEP isAuthenticated/isPaid so existing pages don't break.
 *
 * © 2026 Advanced Advisory Services, LLC. All Rights Reserved.
 */

const PAID_STATUSES = new Set(["active", "active_one_time", "trialing"]);
const SUBSCRIPTION_LOOKUP_ATTEMPTS = 3;
const SUBSCRIPTION_LOOKUP_DELAY_MS = 450;

function safeInt(x, fallback = null) {
  const n = Number(x);
  if (!Number.isFinite(n)) return fallback;
  return Math.trunc(n);
}

function safeBool(x, fallback = false) {
  if (x === true) return true;
  if (x === false) return false;
  return fallback;
}

function uniqStrings(xs) {
  const out = [];
  const seen = new Set();
  (Array.isArray(xs) ? xs : []).forEach((v) => {
    const s = String(v || "").trim();
    if (!s) return;
    if (seen.has(s)) return;
    seen.add(s);
    out.push(s);
  });
  return out;
}

function sleep(ms) {
  return new Promise((resolve) => {
    window.setTimeout(resolve, Number(ms) || 0);
  });
}

function normalizePlanKey(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
}

function isGlobalProPlanKey(value) {
  return normalizePlanKey(value) === "global_pro";
}

function samePlanKey(a, b) {
  const left = normalizePlanKey(a);
  const right = normalizePlanKey(b);
  if (!left || !right) return false;
  return left === right;
}

function chooseBestPlan(candidates) {
  const list = Array.isArray(candidates) ? candidates : [];
  if (!list.length) return null;

  const score = (p) => {
    const maxBiz = safeInt(p?.max_businesses, 0) || 0;
    const consultant = safeBool(p?.allow_consultant_workflow, false) ? 1 : 0;
    const remediation = safeBool(p?.allow_remediation, false) ? 1 : 0;
    const reports = safeBool(p?.allow_reports, false) ? 1 : 0;
    const price = safeInt(p?.price_usd_cents, 0) || 0;
    const t = Date.parse(p?.__sub_created_at || "") || 0;
    return [maxBiz, consultant, remediation, reports, price, t];
  };

  let best = list[0];
  let bestScore = score(best);

  for (let i = 1; i < list.length; i += 1) {
    const s = score(list[i]);
    let better = false;
    for (let k = 0; k < s.length; k += 1) {
      if (s[k] > bestScore[k]) {
        better = true;
        break;
      }
      if (s[k] < bestScore[k]) break;
    }
    if (better) {
      best = list[i];
      bestScore = s;
    }
  }

  return best;
}

function inferTierFromPlan(plan) {
  const maxBiz = safeInt(plan?.max_businesses, 1);
  if (maxBiz != null && maxBiz > 1) return "top";
  if (safeBool(plan?.allow_consultant_workflow, false)) return "top";
  if (isGlobalProPlanKey(plan?.plan_key)) return "top";
  return "single";
}

function applyResolvedState({
  setIsPaid,
  setPaidStatus,
  setTier,
  setRawPlanKey,
  setAllowReports,
  setAllowRemediation,
  setMaxBusinesses,
  setAuditsPerPeriod,
  setPeriod,
  setAllowConsultantWorkflow,
  isPaid,
  paidStatus,
  tier,
  rawPlanKey,
  allowReports,
  allowRemediation,
  maxBusinesses,
  auditsPerPeriod,
  period,
  allowConsultantWorkflow,
}) {
  setIsPaid(Boolean(isPaid));
  setPaidStatus(paidStatus ?? null);
  setTier(String(tier || "").toLowerCase() === "top" ? "top" : "single");
  setRawPlanKey(rawPlanKey ?? null);
  setAllowReports(Boolean(allowReports));
  setAllowRemediation(Boolean(allowRemediation));
  setMaxBusinesses(safeInt(maxBusinesses, 1) ?? 1);
  setAuditsPerPeriod(auditsPerPeriod ?? null);
  setPeriod(period ?? null);
  setAllowConsultantWorkflow(Boolean(allowConsultantWorkflow));
}

async function fetchPaidSubscriptionsWithRetry(uid) {
  let lastError = null;
  let lastRows = [];
  let hadSuccessfulLookup = false;

  for (let attempt = 0; attempt < SUBSCRIPTION_LOOKUP_ATTEMPTS; attempt += 1) {
    try {
      const { data: subs, error: subsErr } = await supabase
        .from("subscriptions")
        .select("status, plan_key, created_at")
        .eq("user_id", uid)
        .in("status", Array.from(PAID_STATUSES))
        .order("created_at", { ascending: false });

      if (subsErr) throw subsErr;

      hadSuccessfulLookup = true;
      const rows = Array.isArray(subs) ? subs : [];
      lastRows = rows;
      if (rows.length > 0) {
        return { rows, error: null };
      }
    } catch (err) {
      lastError = err;
    }

    if (attempt < SUBSCRIPTION_LOOKUP_ATTEMPTS - 1) {
      await sleep(SUBSCRIPTION_LOOKUP_DELAY_MS);
    }
  }

  return {
    rows: lastRows,
    error: hadSuccessfulLookup ? null : lastError || new Error("subscription lookup failed"),
  };
}

async function fetchPlansWithRetry(planKeys) {
  let lastError = null;

  for (let attempt = 0; attempt < SUBSCRIPTION_LOOKUP_ATTEMPTS; attempt += 1) {
    try {
      const { data: plans, error: plansErr } = await supabase
        .from("plans")
        .select(
          "plan_key, is_active, allow_reports, allow_remediation, max_businesses, audits_per_period, period, allow_consultant_workflow, price_usd_cents"
        )
        .in("plan_key", planKeys);

      if (plansErr) throw plansErr;
      return { rows: Array.isArray(plans) ? plans : [], error: null };
    } catch (err) {
      lastError = err;
    }

    if (attempt < SUBSCRIPTION_LOOKUP_ATTEMPTS - 1) {
      await sleep(SUBSCRIPTION_LOOKUP_DELAY_MS);
    }
  }

  return { rows: [], error: lastError || new Error("plan lookup failed") };
}

export default function useEntitlement() {
  const mountedRef = useRef(true);
  const resolutionSeqRef = useRef(0);

  const [loading, setLoading] = useState(true);
  const [resolutionError, setResolutionError] = useState(null);

  // Back-compat fields
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isPaid, setIsPaid] = useState(false);

  // Identity + tier
  const [tier, setTier] = useState("single");
  const [rawPlanKey, setRawPlanKey] = useState(null);

  // Capability flags
  const [allowReports, setAllowReports] = useState(false);
  const [allowRemediation, setAllowRemediation] = useState(false);
  const [maxBusinesses, setMaxBusinesses] = useState(1);
  const [auditsPerPeriod, setAuditsPerPeriod] = useState(null);
  const [period, setPeriod] = useState(null);
  const [allowConsultantWorkflow, setAllowConsultantWorkflow] = useState(false);

  // Optional debug context
  const [paidStatus, setPaidStatus] = useState(null);
  const [userId, setUserId] = useState(null);

  const applyUnauthed = useCallback(() => {
    if (!mountedRef.current) return;

    setUserId(null);
    setIsAuthenticated(false);

    setIsPaid(false);
    setPaidStatus(null);

    setTier("single");
    setRawPlanKey(null);

    setAllowReports(false);
    setAllowRemediation(false);
    setMaxBusinesses(1);
    setAuditsPerPeriod(null);
    setPeriod(null);
    setAllowConsultantWorkflow(false);
    setResolutionError(null);

    setLoading(false);
  }, []);

  const applyUnpaidAuthenticated = useCallback(() => {
    if (!mountedRef.current) return;

    setIsAuthenticated(true);
    applyResolvedState({
      setIsPaid,
      setPaidStatus,
      setTier,
      setRawPlanKey,
      setAllowReports,
      setAllowRemediation,
      setMaxBusinesses,
      setAuditsPerPeriod,
      setPeriod,
      setAllowConsultantWorkflow,
      isPaid: false,
      paidStatus: null,
      tier: "single",
      rawPlanKey: null,
      allowReports: false,
      allowRemediation: false,
      maxBusinesses: 1,
      auditsPerPeriod: null,
      period: null,
      allowConsultantWorkflow: false,
    });
    setResolutionError(null);

    setLoading(false);
  }, []);

  const resolveEntitlement = useCallback(async () => {
    const resolutionId = resolutionSeqRef.current + 1;
    resolutionSeqRef.current = resolutionId;

    const isCurrentResolution = () =>
      mountedRef.current && resolutionSeqRef.current === resolutionId;

    if (!mountedRef.current) return;
    setLoading(true);
    setResolutionError(null);

    let session = null;

    try {
      const { data: sessData, error: sessErr } = await supabase.auth.getSession();
      if (sessErr) throw sessErr;
      session = sessData?.session || null;
    } catch {
      if (!isCurrentResolution()) return;
      setResolutionError("session_lookup_failed");
      setLoading(false);
      return;
    }

    if (!isCurrentResolution()) return;

    const uid = session?.user?.id || null;

    if (!uid) {
      applyUnauthed();
      return;
    }

    setUserId(uid);
    setIsAuthenticated(true);

    /**
     * Local Admin Tools override.
     *
     * localStorage is never authority. We call the certified stateless
     * Platform Security service only when an override is actually present.
     * A failed, unauthorized, malformed, or identity-mismatched capability
     * response is fail-safe: the override is ignored and normal subscription
     * resolution continues unchanged.
     */
    const localOverride = readEntitlementOverride();
    const hasValidOverrideTier =
      localOverride &&
      typeof localOverride === "object" &&
      ["single", "top"].includes(String(localOverride.tier || "").toLowerCase());

    if (hasValidOverrideTier) {
      const capabilityResult =
        await fetchEffectivePlatformSecurityCapabilities();

      if (!isCurrentResolution()) return;

      const mayConsumeOverride =
        capabilityResult.ok === true &&
        capabilityResult.userId === uid &&
        capabilityResult.capabilities.includes("MANAGE_PLATFORM_SECURITY");

      if (mayConsumeOverride) {
        const cleanTier =
          String(localOverride.tier || "").toLowerCase() === "top"
            ? "top"
            : "single";

        applyResolvedState({
          setIsPaid,
          setPaidStatus,
          setTier,
          setRawPlanKey,
          setAllowReports,
          setAllowRemediation,
          setMaxBusinesses,
          setAuditsPerPeriod,
          setPeriod,
          setAllowConsultantWorkflow,
          isPaid: safeBool(localOverride.isPaid, true),
          paidStatus: "local_override",
          tier: cleanTier,
          rawPlanKey:
            localOverride.rawPlanKey ??
            localOverride.planKey ??
            localOverride.plan_key ??
            null,
          allowReports: safeBool(localOverride.allowReports, true),
          allowRemediation: safeBool(localOverride.allowRemediation, true),
          maxBusinesses:
            safeInt(
              localOverride.maxBusinesses,
              cleanTier === "top" ? 25 : 1
            ) ?? (cleanTier === "top" ? 25 : 1),
          auditsPerPeriod: localOverride.auditsPerPeriod ?? null,
          period: localOverride.period ?? null,
          allowConsultantWorkflow: safeBool(
            localOverride.allowConsultantWorkflow,
            cleanTier === "top"
          ),
        });

        setResolutionError(null);
        setLoading(false);
        return;
      }
    }

    // Normal path: fetch active paid subscriptions with a short retry window.
    const { rows: subRows, error: subsError } =
      await fetchPaidSubscriptionsWithRetry(uid);

    if (!isCurrentResolution()) return;

    // A failed lookup is not proof that a paid subscriber is unpaid. Keep the
    // user on a neutral verification state instead of sending them to Pricing.
    if (subsError && subRows.length === 0) {
      setResolutionError("subscription_lookup_failed");
      setLoading(false);
      return;
    }

    if (subRows.length === 0) {
      applyUnpaidAuthenticated();
      return;
    }

    const planKeys = uniqStrings(
      subRows.flatMap((r) => [r?.plan_key, normalizePlanKey(r?.plan_key)])
    );

    if (!planKeys.length) {
      setResolutionError("plan_metadata_unavailable");
      setLoading(false);
      return;
    }

    // Resolve the plan capabilities before publishing the paid access state.
    // This keeps entitlement resolution atomic: the PaywallGuard never sees a
    // half-resolved paid plan with temporary false capability flags.
    const { rows: planRows, error: planError } =
      await fetchPlansWithRetry(planKeys);

    if (!isCurrentResolution()) return;

    if (planError) {
      setResolutionError("plan_lookup_failed");
      setLoading(false);
      return;
    }

    const candidates = planRows
      .map((p) => {
        const matchingSub = subRows.find((s) =>
          samePlanKey(s?.plan_key, p?.plan_key)
        );
        return {
          ...p,
          __sub_created_at: matchingSub?.created_at || null,
          __sub_status: matchingSub?.status || null,
        };
      })
      .filter((p) => safeBool(p?.is_active, true) !== false);

    const best = chooseBestPlan(candidates) || candidates[0] || null;

    if (!best) {
      setResolutionError("plan_metadata_unavailable");
      setLoading(false);
      return;
    }

    const t = inferTierFromPlan(best);

    applyResolvedState({
      setIsPaid,
      setPaidStatus,
      setTier,
      setRawPlanKey,
      setAllowReports,
      setAllowRemediation,
      setMaxBusinesses,
      setAuditsPerPeriod,
      setPeriod,
      setAllowConsultantWorkflow,
      isPaid: true,
      paidStatus: subRows[0]?.status || "active",
      tier: t,
      rawPlanKey: normalizePlanKey(best.plan_key),
      allowReports: safeBool(best.allow_reports, false),
      allowRemediation: safeBool(best.allow_remediation, false),
      maxBusinesses: safeInt(best.max_businesses, 1) ?? 1,
      auditsPerPeriod: best.audits_per_period ?? null,
      period: best.period ?? null,
      allowConsultantWorkflow: safeBool(best.allow_consultant_workflow, false),
    });

    setResolutionError(null);
    setLoading(false);
  }, [applyUnauthed, applyUnpaidAuthenticated]);

  useEffect(() => {
    mountedRef.current = true;
    resolveEntitlement();
    return () => {
      mountedRef.current = false;
      resolutionSeqRef.current += 1;
    };
  }, [resolveEntitlement]);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange(() => {
      resolveEntitlement();
    });

    return () => {
      sub?.subscription?.unsubscribe?.();
    };
  }, [resolveEntitlement]);

  return {
    loading,

    // Back-compat
    isAuthenticated,
    isPaid,

    // New primary fields
    tier,
    isTopTier: tier === "top",
    planKey: rawPlanKey,
    rawPlanKey,

    allowReports,
    allowRemediation,
    maxBusinesses,
    auditsPerPeriod,
    period,
    allowConsultantWorkflow,

    // Access-resolution controls
    error: resolutionError,
    refresh: resolveEntitlement,

    // Debug
    paidStatus,
    userId,
  };
}
